/** @jsxImportSource theme-ui @jsxRuntime classic */
import { Box, Flex, Grid, Spinner, Text } from '@tishman/components';
import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { parseISO, isWithinInterval } from 'date-fns';

import { BuyTicketsLayoutContext } from '../../../layouts/BuyTicketsLayout';
import {
  actions,
  selectComboIndex,
  selectFlow,
  selectFlowBaseProducts,
  selectHasPerformances,
  selectTotalQuantity,
  useAppDispatch,
  useAppSelector,
  selectShopCartSelections,
} from '../../../store';
import { ContinueButton } from '../ContinueButton';
import { Translatable, useTranslations } from '../../../utils/use-translations';
import { isSunsetAvailability } from '../../../services/viva/utils';
import {
  ADDON_EVENTS_WITH_TIME_CAPACITY,
  BosEventForAddOnStatGroup,
  COMBO_FLOWS,
  FLOWS_WITH_DISCOUNTED_TICKETS,
  Flow,
  checkAdaptivePricing,
} from '../../../services/viva/constants';
import { AddToCartButton } from '../../AddToCartButton';
import { useAddOnItems } from '../../../blocks/VivaBuyTickets';
import { TicketType } from '../../../services/viva/types';
import { usePageData } from '../../../utils';

import { TimeSlot } from './TimeSlot';
import { TabMenu } from './TabMenu';

import type { TicketTypesForFlow } from '../../../data/translations/ticketType';
import type { AddOnItem } from '../AddOns';

interface TimePickerProps {
  description?: string;
  isLoading?: boolean;
  isLastStep?: boolean;
  stepNumber: number;
  onContinue?: () => Promise<void>;
}

/**
 * @see useSelectTimeSlot for actual time selection logic
 * @see TimeSlot.tsx for time slot UI component
 **/
export const TimePicker = ({ isLastStep, stepNumber }: TimePickerProps) => {
  const { dayAvailabilities, isLoading, tickets, sunsetTickets } = useContext(
    BuyTicketsLayoutContext,
  );
  const { allSanityTicketAvailabilityOverride } =
    usePageData<Queries.BuyTicketsAllInPassQuery>();

  const dispatch = useAppDispatch();
  const flow = useAppSelector(selectFlow);
  const hasAdaptivePricing = checkAdaptivePricing(flow);
  const shopCart = useAppSelector((state) => state.order.shopCart);
  const hasPerformances = useAppSelector(selectHasPerformances);
  const comboIndex = useAppSelector(selectComboIndex);
  const selectionCount = useAppSelector(selectTotalQuantity);
  const flowBaseProducts = useAppSelector(selectFlowBaseProducts);
  const selectedTimeSlot = useAppSelector(
    (state) => state.order.shopCart.timeSlot,
  );
  const shopCartSelections = useAppSelector(selectShopCartSelections);

  const availablePerformancesHash =
    useAppSelector((state) => state.order.shopCart.addOnsPerformancesHash) ??
    {};

  const translations = useTranslations<TicketTypesForFlow>(
    Translatable.TicketType,
  );
  const continueTranslation = translations?.continue;

  const { addOns, isLoading: areAddOnsLoading } = useAddOnItems({ stepNumber });

  const [localPerformanceAK, setLocalPerformanceAK] = useState('');
  const [isTimeSlotUpdating, setIsTimeSlotUpdating] = useState(false);

  // Sunset ticket logic
  const isSunsetSlotSelected = useCallback(() => {
    const timeSlot = dayAvailabilities?.find(
      (timeSlot) => timeSlot.ak === selectedTimeSlot?.[0]?.ak,
    );
    return timeSlot ? isSunsetAvailability(timeSlot) : false;
  }, [dayAvailabilities, selectedTimeSlot]);

  // This effect ensures that whenever selections change and we have a selected time slot,
  // we update all selections with the time slot information
  useEffect(() => {
    if (
      selectedTimeSlot &&
      selectedTimeSlot.length > 0 &&
      shopCartSelections &&
      shopCartSelections.length > 0
    ) {
      // Check if any selection is missing the time slot
      const needsTimeSlotUpdate = shopCartSelections.some(
        (selection) => !selection.timeSlot || selection.timeSlot.length === 0,
      );

      if (needsTimeSlotUpdate) {
        dispatch(
          actions.order.setShopCartSelectionsTimeSlots(selectedTimeSlot),
        );
      }
    }
  }, [dispatch, selectedTimeSlot, shopCartSelections]);

  useEffect(() => {
    if (!isLoading && !areAddOnsLoading && isTimeSlotUpdating) {
      setIsTimeSlotUpdating(false);
    }
  }, [isLoading, areAddOnsLoading, isTimeSlotUpdating]);

  useEffect(() => {
    if (
      isSunsetSlotSelected() &&
      flow !== Flow.CITY_PASS_REDEMPTION &&
      sunsetTickets
    ) {
      dispatch(actions.order.updateShopCartSelections(sunsetTickets));
    } else if (tickets) {
      if (
        flow === Flow.RINK &&
        selectedTimeSlot?.[0]?.products &&
        selectedTimeSlot?.[0]?.products.length > 0
      ) {
        // Looping through the products that are returned with the performance
        const adaptedProducts = selectedTimeSlot[0].products
          .map((ticket) => {
            // First check if ticket matches a base product
            const baseProduct = flowBaseProducts.find(
              (product) => product.productAK === ticket.ak,
            );

            if (baseProduct) return baseProduct;

            // If no base product match, search through variants
            for (const product of flowBaseProducts) {
              const matchingVariant = product.variants?.find(
                (variant) => variant.productAK === ticket.ak,
              );
              if (matchingVariant) return matchingVariant;
            }
          })
          .filter((product): product is TicketType => product !== undefined);
        dispatch(actions.order.updateShopCartSelections(adaptedProducts));
      } else {
        dispatch(actions.order.updateShopCartSelections(tickets));
      }
    }
    if (
      hasAdaptivePricing &&
      selectedTimeSlot?.[0]?.products &&
      selectedTimeSlot?.[0]?.products.length > 0
    ) {
      if (
        selectedTimeSlot[0].isDiscounted &&
        FLOWS_WITH_DISCOUNTED_TICKETS.includes(flow)
      ) {
        selectedTimeSlot[0]?.discountedProducts?.forEach((product) => {
          dispatch(
            actions.order.updateShopCartSelectionsPrice({
              productAK: product.ak,
              price: parseFloat((parseInt(product.price) * 100).toFixed(2)),
              tax: 0,
              discounted: true,
            }),
          );
        });
      } else {
        selectedTimeSlot[0].products.forEach((product) => {
          dispatch(
            actions.order.updateShopCartSelectionsPrice({
              productAK: product.ak,
              price: parseFloat((parseInt(product.price) * 100).toFixed(2)),
              tax: 0,
            }),
          );
        });
      }
    }
  }, [dispatch, flow, isSunsetSlotSelected, selectedTimeSlot]);

  const addOnShouldShow = (addOn: AddOnItem) => {
    const addOnIsTimed = ADDON_EVENTS_WITH_TIME_CAPACITY.includes(
      BosEventForAddOnStatGroup[addOn.statGroup],
    );
    if (!availablePerformancesHash[addOn.ak])
      return { shouldShow: false, isTimed: false };

    if (
      addOnIsTimed &&
      (availablePerformancesHash[addOn.ak]?.available ?? 0) === 0
    ) {
      return { shouldShow: false, isTimed: false };
    }

    return { shouldShow: true, isTimed: addOnIsTimed };
  };

  const buttonIsDisabled = useMemo(() => {
    if (isLoading || areAddOnsLoading || isTimeSlotUpdating) return true;

    let timeSlotSelected = false;
    if (COMBO_FLOWS.includes(flow) && comboIndex === 1) {
      // For combo, there is a timeslot for each time picker. The combo index is the index of the time picker.
      // If the combo index is 0, then the first time picker is selected, and so on.
      timeSlotSelected = (shopCart.timeSlot ?? []).length === 2;
    } else {
      timeSlotSelected = !!shopCart.timeSlot;
    }

    return (
      !hasPerformances ||
      !shopCart.timeSlot?.length ||
      (!localPerformanceAK && !selectionCount) ||
      !timeSlotSelected
    );
  }, [
    isLoading,
    areAddOnsLoading,
    isTimeSlotUpdating,
    flow,
    comboIndex,
    hasPerformances,
    shopCart.timeSlot,
    localPerformanceAK,
    selectionCount,
  ]);

  const filteredDayAvailabilities = useMemo(() => {
    // Early return if no data is available
    if (
      !dayAvailabilities ||
      !allSanityTicketAvailabilityOverride?.nodes?.[0]?.availableDates
    ) {
      return dayAvailabilities;
    }

    const availableDates =
      allSanityTicketAvailabilityOverride.nodes[0].availableDates;

    // If there are no available dates in the override data, return all availabilities
    if (availableDates.length === 0) {
      return dayAvailabilities;
    }

    // Filter time slots based on the time ranges in the override data
    return dayAvailabilities.filter((timeSlot) => {
      // Skip slots with undefined availability or missing timeRaw
      if (timeSlot.availability === undefined || !timeSlot.timeRaw) {
        return false;
      }

      const availableTimeRange = availableDates.find((dateOverride) => {
        const overrideDate = parseISO(dateOverride?.date ?? '');
        const timeSlotDate = parseISO(timeSlot.timeRaw);

        return (
          overrideDate.getUTCFullYear() === timeSlotDate.getUTCFullYear() &&
          overrideDate.getUTCMonth() === timeSlotDate.getUTCMonth() &&
          overrideDate.getUTCDate() === timeSlotDate.getUTCDate()
        );
      });

      if (!availableTimeRange || !availableTimeRange.timeRanges) {
        return false;
      }

      // If no time ranges specified or empty array, all times are available
      if (availableTimeRange.timeRanges.length === 0) {
        return true;
      }

      return availableTimeRange.timeRanges?.some((timeRange) => {
        if (!timeRange || !timeRange.from || !timeRange.to) {
          return false;
        }

        const isAvailable = isWithinInterval(parseISO(timeSlot.timeRaw), {
          start: parseISO(timeRange.from),
          end: parseISO(timeRange.to),
        });

        return isAvailable;
      });
    });
  }, [dayAvailabilities, allSanityTicketAvailabilityOverride?.nodes]);

  const timeSlots = useMemo(() => {
    return filteredDayAvailabilities?.reduce<React.JSX.Element[]>(
      (acc, timeSlot) => {
        if (timeSlot.availability !== undefined) {
          return [
            ...acc,
            <TimeSlot
              data={timeSlot}
              key={timeSlot.start}
              setLocalPerformanceAK={setLocalPerformanceAK}
            />,
          ];
        }
        return acc;
      },
      [],
    );
  }, [filteredDayAvailabilities, setLocalPerformanceAK]);

  const hasDiscountedPerformances = useMemo(() => {
    return (
      FLOWS_WITH_DISCOUNTED_TICKETS.includes(flow) &&
      dayAvailabilities?.some((timeSlot) => timeSlot.isDiscounted)
    );
  }, [dayAvailabilities]);

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        position: 'relative',
        maxWidth: ['unset', 'unset', '550px'],
        flex: 1,
      }}
    >
      {/* TIME SELECTION UI */}
      {!isLoading && (
        <Fragment>
          <TabMenu hasDiscountedPerformances={hasDiscountedPerformances} />
          {/* HEADING ROW */}
          <Grid
            backgroundColor="black"
            color="white"
            mt={3}
            sx={{
              paddingX: [2, 3],
              paddingY: 2,
              gridTemplateColumns: hasAdaptivePricing ? '1fr 4fr' : '1fr',
            }}
          >
            <Text
              sx={{
                color: 'white',
                fontSize: 1,
                textAlign: 'center',
              }}
              variant="smallP"
            >
              TIME
            </Text>
            {hasAdaptivePricing && (
              <Text
                sx={{ color: 'white', fontSize: 1, textAlign: 'center' }}
                variant="smallP"
              >
                TICKET TYPE
              </Text>
            )}
          </Grid>
          {/* TIME SLOT LISTING */}
          <Flex
            role="listbox"
            sx={{
              borderBottom: '1px solid black',
              borderTop: '1px solid black',
              flexDirection: 'column',
              gap: 3,
              maxHeight: '320px',
              overflow: 'auto',
              position: 'relative',
              display: 'block',
            }}
          >
            {timeSlots?.length ? (
              timeSlots
            ) : (
              <Text
                sx={{
                  fontSize: 1,
                  paddingY: 2,
                  textAlign: 'center',
                }}
                variant="smallP"
              >
                No times available for this date
              </Text>
            )}
          </Flex>
        </Fragment>
      )}
      {/* CONTINUE BUTTON */}
      <Box
        sx={{
          position: 'relative',
          marginTop: 3,
          gridColumnStart: 2,
          flex: 1,
        }}
      >
        {isLastStep ||
        !addOns.some((addOn) => addOnShouldShow(addOn).shouldShow) ? (
          <AddToCartButton
            disabled={!selectedTimeSlot || buttonIsDisabled}
            stepNumber={stepNumber}
          />
        ) : (
          <ContinueButton
            disabled={buttonIsDisabled}
            onClick={() => {
              // If the time slot is selected, ensure it's applied to all selections
              if (selectedTimeSlot) {
                setIsTimeSlotUpdating(true);

                dispatch(
                  actions.order.setShopCartSelectionsTimeSlots(
                    selectedTimeSlot,
                  ),
                );

                setTimeout(() => {
                  dispatch(actions.wizard.setNextStep());
                  setIsTimeSlotUpdating(false);
                }, 50);
              } else {
                dispatch(actions.wizard.setNextStep());
              }
            }}
            sx={{
              width: '100%',
            }}
            title={continueTranslation}
          />
        )}

        {(isLoading || areAddOnsLoading || isTimeSlotUpdating) && (
          <Spinner
            sx={{
              display: 'block',
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          />
        )}
      </Box>
    </Flex>
  );
};
